// @flow
import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Input,
  Modal,
  Upload,
  Icon,
  Select,
} from 'antd';
import {
  axiosAuthInstance,
  API_END_POINTS,
  axiosEmptyInstance,
} from 'api';
import {
  MESSAGES,
} from 'appconstants';
import { notificationActions } from 'store/actions';
import { NOTIFICATION } from 'store/actionTypes';
import {
  SpH5,
  SpError,
  SpButton,
  SpCaption,
} from 'components/DesignKit';
import SimpleReactValidator from 'simple-react-validator';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

type Props = {
  close: Function,
  submit: Function,
  visible: boolean,
  submitting: boolean,
  setNotification: Function,
  representatives: Array<Object>,
};

const AddDocument = (props: Props) => {
  const {
    visible,
    close,
    submit,
    submitting,
    setNotification,
    representatives,
  } = props;

  const [, forceUpdate] = useState();
  const [type, setType] = useState('VERIFICATION');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const maxWords = 200;
  const [documentUrl, setDocumentUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [usBusinessRepGid, setUsBusinessRepGid] = useState('');
  const simpleValidator = useRef(new SimpleReactValidator());
  const [disableButton, setDisableButton] = useState(false);

  useEffect(() => {
    if (!submitting) {
      setDisableButton(false);
    }
  }, [submitting]);

  const updateWordCount = (remainingWords) => {
    const wordCountElement = document.getElementById('wordCount');
    wordCountElement.textContent = `${remainingWords} word${remainingWords !== 1 ? 's' : ''} left`;
  };

  const handleWordChange = (event) => {
    const inputText = event.target.value;
    const words = inputText.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) {
      setDescription(inputText);
      updateWordCount(maxWords - words.length);
    }
  };

  const fileReader = async file => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsArrayBuffer(file);
  });

  const handleChange = async info => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    try {
      setDocumentUrl('');
      setLoading(true);
      const file = info.file.originFileObj;
      if (file) {
        const { data: { entity } } = await axiosAuthInstance.get(API_END_POINTS.UPLOAD_CUSTOM_DOCUMENT_REQUEST);
        await axiosEmptyInstance.put(entity, await fileReader(file), { headers: { 'Content-Type': file.type } });
        const documentDetails = {
          keyType: 'CUSTOM_DOCUMENT_REQUEST',
          fileUrl: entity,
        };
        const { data: { entity: { fileUrl } } } = await axiosAuthInstance.post(API_END_POINTS.UPLOAD_CUSTOM_DOCUMENT_REQUEST, documentDetails);
        setDocumentUrl(fileUrl);
        setFileName(info.file.name);
        setNotification({
          type: NOTIFICATION.SUCCESS,
          payload: MESSAGES.CUSTOM_DOCUMENT.UPDATE_SUCCESS,
        });
      }
    } catch (error) {
      setDocumentUrl('');
      setFileName('');
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: MESSAGES.CUSTOM_DOCUMENT.FAILED,
      });
    } finally {
      setLoading(false);
    }
  };

  const validate = (event) => {
    setDisableButton(true);
    event.preventDefault();
    const formValid = simpleValidator.current.allValid();
    setDisableButton(formValid);
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    submit({
      name,
      type,
      description,
      documentUrl,
      usBusinessRepGid: (type === 'US_BUSINESS_REP_VERIFICATION' && usBusinessRepGid) ? usBusinessRepGid : null,
      email: (type === 'OTHER' && email) ? email : null,
    });
  };

  return (
    <Modal
      centered
      closable={false}
      visible={visible}
      footer={[
        <SpButton onClick={() => close()} type="secondary">Cancel</SpButton>,
        <SpButton onClick={validate} disabled={disableButton}>Submit Request</SpButton>,
      ]}
      title="New Document Request"
    >
      <div className="px-4">
        <div className={submitting ? 'OVERLAY' : ''} />
        <div className="mb-3">
          <label htmlFor=""><SpH5>Type</SpH5></label>
          <Select
            placeholder="Select a type"
            className="w-100"
            value={type}
            onChange={(e) => setType(e)}
          >
            <Option key={1} value="VERIFICATION">VERIFICATION</Option>
            <Option key={2} value="US_BUSINESS_REP_VERIFICATION">BUSINESS REPRESENTATIVE</Option>
            <Option key={3} value="OTHER">OTHER</Option>
          </Select>
          <SpError>
            {simpleValidator.current.message('type', type, 'required')}
          </SpError>
        </div>
        {(type === 'US_BUSINESS_REP_VERIFICATION') && (
          <div className="mb-3">
            <label htmlFor=""><SpH5>Email ID</SpH5></label>
            <Select
              placeholder="Select a type"
              className="w-100"
              value={usBusinessRepGid}
              onChange={(e) => setUsBusinessRepGid(e)}
            >
              {representatives?.map((item) => (
                <Option key={item?.gid} value={item?.gid}>{item?.email}</Option>
              ))}
            </Select>
            <SpError>
              {simpleValidator.current.message('email', usBusinessRepGid, 'required')}
            </SpError>
          </div>
        )}
        <div className="mb-3">
          <label htmlFor=""><SpH5>Name</SpH5></label>
          <Input
            placeholder="Enter a name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />

          <SpError>
            {simpleValidator.current.message('name', name, 'required')}
          </SpError>
        </div>
        {type === 'OTHER' && (
          <>
            <div className="mb-3">
              <label htmlFor=""><SpH5>Email ID</SpH5></label>
              <Input
                placeholder="Enter a email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <SpError>
                {simpleValidator.current.message('email', email, 'required')}
              </SpError>
            </div>
            <div className="mb-3">
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 16 }}>
                <InfoCircleOutlined style={{
                  color: 'rgb(255 167 0)', marginRight: 8, marginTop: 3, fontSize: 20,
                }}
                />
                <span>
                  Please ensure the email address entered is relevant to the business account.
                </span>
              </div>
            </div>
          </>
        )}

        <div className="mb-3">
          <label htmlFor=""><SpH5>Description</SpH5></label>
          <TextArea
            placeholder="Enter description"
            rows={6}
            value={description}
            onChange={handleWordChange}
          />
          <SpCaption>
            <div id="wordCount">{maxWords - description.split(/\s+/).filter(Boolean).length} words left</div>
          </SpCaption>
          <SpError>
            {simpleValidator.current.message('description', description, 'required')}
          </SpError>
        </div>
        <div>
          <label htmlFor=""><SpH5>Upload Document (optional)</SpH5></label>
          <Input
            placeholder="Upload a file"
            value={fileName}
            suffix={(
              <>
                <Upload
                  name="avatar"
                  className="avatar-uploader"
                  onChange={handleChange}
                  showUploadList={false}
                >
                  <Icon type={loading ? 'loading' : 'upload'} />
                </Upload>
              </>
            )}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
});

const mapDispatchToProps = (dispatch) => ({
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(AddDocument);
